import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronDown, ChevronUp, MapPin, Truck, CreditCard, Smartphone, ShieldCheck, Check, Lock, Package, Award, Video, Headphones } from "lucide-react";
import { useCart } from "../context/CartContext";
import CdekPvzPicker from "../components/CdekPvzPicker";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const formatRub = (n) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(n) + " ₽";

const PHONE_RE = /^\+?\d[\d\s\-\(\)]{7,18}\d$/;
const EMAIL_RE = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

export default function Checkout() {
  const navigate = useNavigate();
  const { items, itemsCount, itemsTotal, clearCart } = useCart();

  const [customer, setCustomer] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("shoemaster_customer_v1")) || {};
    } catch {
      return {};
    }
  });
  const update = (k, v) => setCustomer((c) => ({ ...c, [k]: v }));

  const [deliveryMethod, setDeliveryMethod] = useState("pvz");
  const [paymentMethod, setPaymentMethod] = useState("sbp");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [selectedPvz, setSelectedPvz] = useState(null);
  const [showItems, setShowItems] = useState(false);

  const [doorCalc, setDoorCalc] = useState({ cost: 0, zone: null, matched: false, loading: false });

  const calcTimer = useRef(null);
  useEffect(() => {
    if (deliveryMethod !== "door") return;
    const city = (customer.city || "").trim();
    if (city.length < 2) {
      setDoorCalc({ cost: 0, zone: null, matched: false, loading: false });
      return;
    }
    setDoorCalc((s) => ({ ...s, loading: true }));
    if (calcTimer.current) clearTimeout(calcTimer.current);
    calcTimer.current = setTimeout(() => {
      fetch(`${API_URL}/api/delivery/calculate?method=door&city=${encodeURIComponent(city)}`)
        .then((r) => r.json())
        .then((d) => setDoorCalc({ cost: d.cost, zone: d.zone, matched: d.matched, loading: false }))
        .catch(() => setDoorCalc({ cost: 0, zone: null, matched: false, loading: false }));
    }, 300);
    return () => { if (calcTimer.current) clearTimeout(calcTimer.current); };
  }, [deliveryMethod, customer.city]);

  useEffect(() => {
    if (deliveryMethod !== "pvz") return;
    setSelectedPvz(null);
  }, [customer.city, deliveryMethod]);

  const deliveryCost = deliveryMethod === "pvz" ? 0 : (doorCalc.matched ? doorCalc.cost : 0);
  const total = useMemo(
    () => Math.max(0, Math.round((itemsTotal + deliveryCost) * 100) / 100),
    [itemsTotal, deliveryCost]
  );

  if (itemsCount === 0 && !submitting) {
    return (
      <div className="min-h-[100dvh] bg-[#F5F0EB] flex flex-col items-center justify-center px-5 gap-4" data-testid="checkout-empty">
        <p className="text-[#1A1A1A]/55 text-sm font-sans">Корзина пуста</p>
        <button
          onClick={() => navigate("/")}
          className="px-8 py-3 bg-[#1A1A1A] text-white text-sm font-sans font-medium rounded-full"
        >
          К акциям
        </button>
      </div>
    );
  }

  const validate = () => {
    if (!customer.lastName?.trim()) return "Укажите фамилию";
    if (!customer.firstName?.trim()) return "Укажите имя";
    if (!customer.middleName?.trim()) return "Укажите отчество";
    if (!PHONE_RE.test(customer.phone || "")) return "Некорректный телефон";
    if (!EMAIL_RE.test(customer.email || "")) return "Некорректный email";
    if (!customer.city?.trim()) return "Укажите город";
    if (deliveryMethod === "pvz") {
      if (!selectedPvz) return "Выберите пункт выдачи СДЭК";
    } else {
      if (!customer.address?.trim()) return "Укажите адрес доставки";
      if (!doorCalc.matched) return `Доставка по адресу не настроена для города "${customer.city}"`;
    }
    return "";
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      localStorage.setItem("shoemaster_customer_v1", JSON.stringify(customer));

      const body = {
        items: items.map((it) => ({
          productId: it.productId, productName: it.productName, brand: it.brand,
          image: it.image, videoUrl: it.videoUrl || null, salePrice: it.salePrice, quantity: it.quantity,
          size: it.size, eventId: it.eventId, eventTitle: it.eventTitle,
        })),
        customer: {
          lastName: customer.lastName.trim(),
          firstName: customer.firstName.trim(),
          middleName: customer.middleName?.trim() || null,
          phone: customer.phone.trim(),
          email: customer.email.trim(),
          city: customer.city.trim(),
          address: customer.address?.trim() || null,
        },
        delivery: deliveryMethod === "pvz"
          ? {
              method: "pvz",
              pvzCode: selectedPvz.code,
              pvzAddress: selectedPvz.addressFull || selectedPvz.address,
              city: selectedPvz.city || customer.city.trim(),
              cost: 0,
            }
          : {
              method: "door",
              city: customer.city.trim(),
              address: customer.address?.trim() || null,
              cost: doorCalc.cost,
              zone: doorCalc.zone,
            },
        paymentMethod,
        discount: 0,
      };

      const res = await fetch(`${API_URL}/api/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail;
        const msg = typeof detail === "string" ? detail
          : Array.isArray(detail) ? detail.map((d) => d.msg || d.message || JSON.stringify(d)).join("; ")
          : typeof detail === "object" && detail ? JSON.stringify(detail)
          : "Не удалось создать заказ";
        throw new Error(msg);
      }
      if (data.paymentRedirect && data.paymentRedirect.url) {
        const { method, url, data: params } = data.paymentRedirect;
        if (method === "POST") {
          const form = document.createElement("form");
          form.method = "POST";
          form.action = url;
          for (const [k, v] of Object.entries(params || {})) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = k;
            input.value = v == null ? "" : String(v);
            form.appendChild(input);
          }
          document.body.appendChild(form);
          form.submit();
          return;
        }
        const qs = new URLSearchParams(params || {}).toString();
        window.location.href = qs ? `${url}?${qs}` : url;
        return;
      }

      if (data.paymentError) {
        setError(`Ошибка оплаты: ${data.paymentError}`);
        setSubmitting(false);
        return;
      }

      clearCart();
      navigate(
        `/order/${encodeURIComponent(data.orderNumber)}?phone=${encodeURIComponent(customer.phone)}`,
        { replace: true }
      );
    } catch (e) {
      setError(e.message || "Ошибка");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#F5F0EB]" data-testid="checkout-page">
      <div className="w-full max-w-[480px] mx-auto">
      <header className="px-5 pt-3 pb-3 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center -ml-2"
          data-testid="checkout-back"
        >
          <ChevronLeft size={24} className="text-[#1A1A1A]" strokeWidth={1.5} />
        </button>
        <div className="flex flex-col items-center">
          <h1 className="text-[18px] font-serif font-bold text-[#1A1A1A]">Оформление заказа</h1>
          <p className="text-[11px] text-[#1A1A1A]/45 font-sans flex items-center gap-1">
            <ShieldCheck size={11} strokeWidth={1.5} />
            Безопасно
          </p>
        </div>
        <div className="w-10" />
      </header>

      <div className="px-5 flex flex-col gap-4 pb-44">
        {/* Order summary with expand */}
        <button
          onClick={() => setShowItems(!showItems)}
          className="bg-white rounded-2xl p-4 flex items-center justify-between w-full text-left"
          data-testid="checkout-summary"
        >
          <div className="flex items-center gap-1 -space-x-2">
            {items.slice(0, 3).map((it) => (
              <div key={`${it.productId}-${it.size}`} className="w-10 h-12 rounded-lg overflow-hidden border-2 border-white bg-[#E8E3DE]">
                {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover" />
                  : it.videoUrl ? <video src={`${it.videoUrl}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                  : null}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <span className="block text-[12px] text-[#1A1A1A]/55 font-sans">
                {itemsCount} {itemsCount === 1 ? "товар" : itemsCount > 1 && itemsCount < 5 ? "товара" : "товаров"}
              </span>
              <span className="block text-[16px] font-sans font-bold text-[#1A1A1A]">{formatRub(itemsTotal)}</span>
            </div>
            {showItems ? <ChevronUp size={18} className="text-[#1A1A1A]/40" /> : <ChevronDown size={18} className="text-[#1A1A1A]/40" />}
          </div>
        </button>

        {showItems && (
          <div className="bg-white rounded-2xl p-4 -mt-2 space-y-3">
            {items.map((it) => (
              <div key={`${it.productId}-${it.size}`} className="flex gap-3">
                <div className="w-10 h-12 rounded-lg overflow-hidden bg-[#E8E3DE] shrink-0">
                  {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.16em] text-[#1A1A1A]/55 font-serif truncate">{it.brand}</p>
                  <p className="text-[12px] font-sans text-[#1A1A1A] truncate">{it.productName}</p>
                </div>
                <span className="text-[13px] font-sans font-semibold text-[#1A1A1A]">{formatRub(it.salePrice * it.quantity)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Contact section */}
        <SectionCard title="Контактные данные" icon="contact">
          <div className="grid grid-cols-2 gap-2">
            <LabeledInput label="Фамилия" value={customer.lastName || ""} onChange={(v) => update("lastName", v)} testId="ck-last" />
            <LabeledInput label="Имя" value={customer.firstName || ""} onChange={(v) => update("firstName", v)} testId="ck-first" />
          </div>
          <div className="mt-2">
            <LabeledInput label="Отчество" value={customer.middleName || ""} onChange={(v) => update("middleName", v)} testId="ck-middle" />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <LabeledInput label="Телефон" value={customer.phone || ""} onChange={(v) => update("phone", v)} type="tel" testId="ck-phone" placeholder="+7 (999) 123-45-67" />
            <LabeledInput label="Email" value={customer.email || ""} onChange={(v) => update("email", v)} type="email" testId="ck-email" placeholder="email@example.com" />
          </div>
          <div className="mt-2">
            <LabeledInput label="Город" value={customer.city || ""} onChange={(v) => update("city", v)} testId="ck-city" />
          </div>
          <p className="text-[10px] text-[#1A1A1A]/40 font-sans mt-2 flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full bg-[#1A1A1A]/10 flex items-center justify-center text-[8px]">i</span>
            Для расчёта точных сроков доставки укажите корректные данные
          </p>
        </SectionCard>

        {/* Delivery section */}
        <SectionCard title="Доставка" icon="delivery">
          <RadioRow
            checked={deliveryMethod === "pvz"}
            onSelect={() => setDeliveryMethod("pvz")}
            icon={<MapPin size={18} strokeWidth={1.6} />}
            label="Пункт выдачи СДЭК"
            sub="Самовывоз из ПВЗ"
            tail={<span className="text-[12px] font-sans font-medium text-emerald-600">Бесплатно</span>}
            testId="delivery-pvz"
          />
          {deliveryMethod === "pvz" && (
            <div className="mt-2 mb-2">
              {!customer.city?.trim() ? (
                <div className="ml-9 p-3 rounded-xl bg-[#F5F0EB] text-[12px] text-[#1A1A1A]/55 font-sans">
                  Сначала укажите город в контактных данных
                </div>
              ) : selectedPvz ? (
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-start gap-2" data-testid="pvz-selected">
                  <Check size={14} className="text-emerald-700 mt-0.5 shrink-0" strokeWidth={2.5} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-700 font-serif font-semibold">ПВЗ СДЭК {selectedPvz.code}</p>
                    <p className="text-[13px] font-sans text-[#1A1A1A] mt-0.5">{selectedPvz.address || selectedPvz.name}</p>
                  </div>
                  <button
                    onClick={() => setSelectedPvz(null)}
                    className="text-[12px] font-sans text-[#1A1A1A]/55 underline"
                  >
                    Сменить
                  </button>
                </div>
              ) : (
                <CdekPvzPicker
                  city={customer.city.trim()}
                  selectedCode={selectedPvz?.code}
                  onSelect={setSelectedPvz}
                />
              )}
            </div>
          )}

          <RadioRow
            checked={deliveryMethod === "door"}
            onSelect={() => setDeliveryMethod("door")}
            icon={<Truck size={18} strokeWidth={1.6} />}
            label="Курьерская доставка"
            sub="Доставка курьером до двери"
            tail={
              <span className="text-[12px] font-sans font-medium text-[#1A1A1A]">
                {deliveryMethod !== "door"
                  ? "от 499 ₽"
                  : doorCalc.loading
                  ? "..."
                  : doorCalc.matched
                  ? formatRub(doorCalc.cost)
                  : "—"}
              </span>
            }
            testId="delivery-door"
          />
          {deliveryMethod === "door" && (
            <div className="ml-9 mt-2 mb-2 grid grid-cols-1 gap-2">
              <LabeledInput label="Адрес" placeholder="Улица, дом, кв." value={customer.address || ""} onChange={(v) => update("address", v)} testId="ck-address" />
              {!doorCalc.loading && customer.city?.trim() && !doorCalc.matched && (
                <p className="text-[11px] font-sans text-red-600">
                  Доставка по адресу не настроена для города "{customer.city}". Выберите ПВЗ или свяжитесь с менеджером.
                </p>
              )}
            </div>
          )}
        </SectionCard>

        {/* Payment section */}
        <SectionCard title="Оплата" icon="payment">
          <RadioRow
            checked={paymentMethod === "card"}
            onSelect={() => setPaymentMethod("card")}
            icon={<CreditCard size={18} strokeWidth={1.6} />}
            label="Оплата картой"
            sub="Visa, Mastercard, МИР и другие"
            tail={
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-bold text-[#1A1F71] bg-[#1A1F71]/10 px-1 py-0.5 rounded">VISA</span>
                <span className="text-[10px] font-bold text-[#EB001B] bg-[#EB001B]/10 px-1 py-0.5 rounded">MC</span>
                <span className="text-[10px] font-bold text-[#4DB45E] bg-[#4DB45E]/10 px-1 py-0.5 rounded">МИР</span>
              </div>
            }
            testId="pay-card"
          />
          <RadioRow
            checked={paymentMethod === "sbp"}
            onSelect={() => setPaymentMethod("sbp")}
            icon={<Smartphone size={18} strokeWidth={1.6} />}
            label="Оплата через СБП"
            sub="Быстрая оплата через ваш банк"
            tail={
              <span className="text-[10px] font-bold text-[#5B2D8E] bg-[#5B2D8E]/10 px-1.5 py-0.5 rounded">СБП</span>
            }
            testId="pay-sbp"
          />
        </SectionCard>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-600 text-sm font-sans" data-testid="checkout-error">
            {error}
          </div>
        )}

        {/* Trust badges */}
        <div className="grid grid-cols-4 gap-2 mt-1">
          <TrustBadge icon={<Package size={16} strokeWidth={1.4} />} text="Быстрая доставка из США" />
          <TrustBadge icon={<Award size={16} strokeWidth={1.4} />} text="100% оригинал гарантировано" />
          <TrustBadge icon={<Video size={16} strokeWidth={1.4} />} text="Живые обзоры из Mailano" />
          <TrustBadge icon={<Headphones size={16} strokeWidth={1.4} />} text="Мы всегда на связи" />
        </div>
      </div>

      </div>
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#1A1A1A]/8 z-20" data-testid="checkout-footer">
        <div className="w-full max-w-[480px] mx-auto px-5 py-3">
          <div className="flex justify-between items-baseline mb-2">
            <span className="text-[13px] font-sans text-[#1A1A1A]/55">Итого к оплате</span>
            <span className="text-[20px] font-sans font-bold text-[#1A1A1A]">{formatRub(total)}</span>
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full bg-[#1A1A1A] text-white font-sans font-semibold py-4 rounded-full text-[15px] active:scale-[0.98] disabled:opacity-50 transition-transform flex items-center justify-center gap-2"
            data-testid="checkout-pay"
          >
            <Lock size={15} strokeWidth={2} />
            {submitting ? "Создаём заказ..." : `Оплатить ${formatRub(total)}`}
          </button>
          <p className="text-[10px] text-[#1A1A1A]/35 font-sans text-center mt-2 leading-tight">
            Нажимая "Оплатить", вы соглашаетесь с условиями{" "}
            <a href="/legal/offer" className="underline">оферты</a> и{" "}
            <a href="/legal/privacy" className="underline">политикой конфиденциальности</a>
          </p>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, icon, children }) {
  const iconEl = icon === "contact" ? "👤" : icon === "delivery" ? "📦" : icon === "payment" ? "💳" : "📋";
  return (
    <section className="bg-white rounded-2xl p-4">
      <h2 className="text-[14px] font-sans font-semibold mb-3 text-[#1A1A1A] flex items-center gap-2">
        <span className="text-[14px]">{iconEl}</span> {title}
      </h2>
      {children}
    </section>
  );
}

function LabeledInput({ label, value, onChange, type = "text", testId, placeholder }) {
  return (
    <div className="relative">
      {value && (
        <span className="absolute top-1.5 left-3 text-[9px] font-sans text-[#1A1A1A]/40">{label}</span>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || label}
        className={`w-full bg-[#F5F0EB] rounded-xl px-3 text-[14px] font-sans text-[#1A1A1A] placeholder:text-[#1A1A1A]/35 focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]/15 ${value ? "pt-5 pb-2" : "py-3"}`}
        data-testid={testId}
      />
    </div>
  );
}

function RadioRow({ checked, onSelect, icon, label, sub, tail, testId }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full flex items-center gap-3 py-2.5 text-left ${checked ? "" : "opacity-80"}`}
      data-testid={testId}
    >
      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${checked ? "border-[#1A1A1A]" : "border-[#1A1A1A]/20"}`}>
        {checked && <span className="w-2.5 h-2.5 rounded-full bg-[#1A1A1A]" />}
      </span>
      <span className="text-[#1A1A1A]/55">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-[14px] font-sans font-medium text-[#1A1A1A]">{label}</span>
        {sub && <span className="block text-[11px] font-sans text-[#1A1A1A]/55">{sub}</span>}
      </span>
      {tail && <span className="shrink-0">{tail}</span>}
    </button>
  );
}

function TrustBadge({ icon, text }) {
  return (
    <div className="flex flex-col items-center text-center gap-1.5">
      <span className="text-[#1A1A1A]/50">{icon}</span>
      <span className="text-[9px] font-sans text-[#1A1A1A]/50 leading-tight">{text}</span>
    </div>
  );
}
